import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faUsers, 
  faSpinner, 
  faSearch,
  faFilter,
  faArchive,
  faEye,
  faEdit,
  faUser,
  faCalendarAlt,
  faUserTag,
  faExclamationTriangle,
  faCircleCheck,
  faCircleXmark,
  faCircleExclamation,
  faChevronLeft,
  faChevronRight,
  faFileExcel
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../Api';

const Supervisors = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user: currentUser } = useAuth();
  const [supervisors, setSupervisors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [villeFilter, setVilleFilter] = useState('all');
  const [regionFilter, setRegionFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [exporting, setExporting] = useState(false);

  // Handle success message from navigation state
  useEffect(() => {
    if (location.state?.successMessage) {
      setSuccessMessage(location.state.successMessage);
      // Clear the success message after 5 seconds
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [location.state]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset to page 1 when debounced search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm]);

  useEffect(() => {
    const isSearch = debouncedSearchTerm !== '' || statusFilter !== 'all' || villeFilter !== 'all' || regionFilter !== 'all';
    fetchSupervisors(isSearch);
  }, [currentPage, debouncedSearchTerm, statusFilter, villeFilter, regionFilter]);

  const fetchSupervisors = async (isSearch = false) => {
    try {
      if (isSearch) {
        setSearchLoading(true);
      } else {
        setLoading(true);
      }
      
      const params = new URLSearchParams({
        page: currentPage,
        per_page: 10
      });
      
      if (debouncedSearchTerm) {
        params.append('search', debouncedSearchTerm);
      }
      
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      
      if (villeFilter !== 'all') {
        params.append('ville', villeFilter);
      }
      
      if (regionFilter !== 'all') {
        params.append('region', regionFilter);
      }
      
      const url = `/supervisors?${params.toString()}`;
      const response = await api.get(url);
      setSupervisors(response.data.data || response.data);
      setTotalPages(response.data.last_page || 1);
      setTotalCount(response.data.total || response.data.length);
    } catch (error) {
      console.error('Error fetching supervisors:', error);
      setError('Erreur lors du chargement des prestataires');
    } finally {
      if (isSearch) {
        setSearchLoading(false);
      } else {
        setLoading(false);
      }
    }
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setCurrentPage(1);
  };

  const handleVilleFilter = (ville) => {
    setVilleFilter(ville);
    setCurrentPage(1);
  };

  const handleRegionFilter = (region) => {
    setRegionFilter(region);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (debouncedSearchTerm) params.append('search', debouncedSearchTerm);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (villeFilter !== 'all') params.append('ville', villeFilter);
      if (regionFilter !== 'all') params.append('region', regionFilter);

      const res = await api.get(`/supervisors/export?${params.toString()}`, {
        responseType: 'blob',
        timeout: 300000,
      });

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      let filename = 'prestataires.xlsx';
      const cd = res.headers['content-disposition'];
      if (cd) {
        const m = cd.match(/filename="?([^"]+)"?/i);
        if (m) filename = m[1];
      }
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError('Erreur lors de l\'export');
    } finally {
      setExporting(false);
    }
  };

  const handleRowClick = (supervisorId) => {
    navigate(`/supervisors/${supervisorId}`);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <FontAwesomeIcon icon={faCircleCheck} className="text-green-500" />;
      case 'suspended':
        return <FontAwesomeIcon icon={faCircleXmark} className="text-red-500" />;
      case 'inactive':
        return <FontAwesomeIcon icon={faCircleExclamation} className="text-yellow-500" />;
      default:
        return <FontAwesomeIcon icon={faCircleXmark} className="text-gray-400" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active':
        return 'Actif';
      case 'suspended':
        return 'Suspendu';
      case 'inactive':
        return 'Inactif (Automatique)';
      default:
        return 'Inconnu';
    }
  };

  const getSupervisorCity = (supervisor) => {
    // Priority: city_name (from city relationship) > custom_location_name > ville array > '-'
    if (supervisor.city_name) {
      return supervisor.city_name;
    }
    if (supervisor.custom_location_name) {
      return supervisor.custom_location_name;
    }
    if (supervisor.ville && Array.isArray(supervisor.ville) && supervisor.ville.length > 0) {
      return supervisor.ville[0]; // Show first city from array
    }
    return '-';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 relative overflow-hidden">
        {/* Dotted Background */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
            backgroundSize: '20px 20px'
          }}></div>
        </div>
        <div className="text-center relative z-10">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des prestataires...</p>
        </div>
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden">
      {/* Dotted Background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }}></div>
      </div>

      {/* Header */}
      <header className="relative z-10 p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">PRESTATAIRES</h1>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleExport}
              disabled={exporting}
              className="px-6 py-3 bg-green-600 text-white rounded-xl transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 active:scale-95 active:shadow-md active:translate-y-0 flex items-center justify-center disabled:opacity-50"
            >
              <FontAwesomeIcon icon={exporting ? faSpinner : faFileExcel} className={`mr-2 ${exporting ? 'animate-spin' : ''}`} />
              {exporting ? 'Export...' : 'Exporter Excel'}
            </button>
            {!['administrateur_general', 'charge_etude', 'charge_etude_superieur', 'resp_controle'].includes(currentUser?.role) && (
              <>
                <button
                  onClick={() => navigate('/supervisors/create')}
                className="px-6 py-3 bg-red-600 text-white rounded-xl transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 active:scale-95 active:shadow-md active:translate-y-0 flex items-center justify-center"
              >
                <FontAwesomeIcon icon={faPlus} className="mr-2" />
                Nouveau Prestataire
              </button>
              <button
                onClick={() => navigate('/supervisors-archived')}
                className="px-6 py-3 bg-gray-600 text-white rounded-xl transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 active:scale-95 active:shadow-md active:translate-y-0 flex items-center justify-center"
              >
                <FontAwesomeIcon icon={faArchive} className="mr-2" />
                Voir les Archivés
              </button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Main content */}
      <div className="relative z-10 p-4 sm:p-6">
        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            <div className="flex items-center">
              <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
              {error}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-4 sm:p-6 mb-6">
          <div className="space-y-4">
            {/* Search Section */}
            <div className="w-full">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Rechercher par nom, email ou référence..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none w-full pr-10"
                />
                {searchLoading && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <FontAwesomeIcon 
                      icon={faSpinner} 
                      className="animate-spin text-red-500 w-4 h-4" 
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Filter Section */}
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <FontAwesomeIcon icon={faFilter} className="text-gray-500" />
                <span className="font-medium text-gray-700">Filtres:</span>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {/* Status Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Statut</label>
                  <select
                    value={statusFilter}
                    onChange={(e) => handleStatusFilter(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                  >
                    <option value="all">Tous les statuts</option>
                    <option value="active">Actif</option>
                    <option value="suspended">Suspendu</option>
                    <option value="inactive">Inactif</option>
                  </select>
                </div>

                {/* Region Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Région</label>
                  <select
                    value={regionFilter}
                    onChange={(e) => handleRegionFilter(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                  >
                    <option value="all">Toutes les régions</option>
                    <option value="Casablanca-Settat">Casablanca-Settat</option>
                    <option value="Rabat-Salé-Kénitra">Rabat-Salé-Kénitra</option>
                    <option value="Marrakech-Safi">Marrakech-Safi</option>
                    <option value="Fès-Meknès">Fès-Meknès</option>
                    <option value="Tanger-Tétouan-Al Hoceïma">Tanger-Tétouan-Al Hoceïma</option>
                    <option value="Oriental">Oriental</option>
                    <option value="Béni Mellal-Khénifra">Béni Mellal-Khénifra</option>
                    <option value="Drâa-Tafilalet">Drâa-Tafilalet</option>
                    <option value="Souss-Massa">Souss-Massa</option>
                    <option value="Guelmim-Oued Noun">Guelmim-Oued Noun</option>
                    <option value="Laâyoune-Sakia El Hamra">Laâyoune-Sakia El Hamra</option>
                    <option value="Dakhla-Oued Ed-Dahab">Dakhla-Oued Ed-Dahab</option>
                  </select>
                </div>

                {/* Ville Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Ville</label>
                  <select
                    value={villeFilter}
                    onChange={(e) => handleVilleFilter(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                  >
                    <option value="all">Toutes les villes</option>
                    <option value="Casablanca">Casablanca</option>
                    <option value="Rabat">Rabat</option>
                    <option value="Marrakech">Marrakech</option>
                    <option value="Fès">Fès</option>
                    <option value="Agadir">Agadir</option>
                    <option value="Tanger">Tanger</option>
                    <option value="Oujda">Oujda</option>
                    <option value="Meknès">Meknès</option>
                    <option value="Mohammedia">Mohammedia</option>
                    <option value="Settat">Settat</option>
                    <option value="El Jadida">El Jadida</option>
                    <option value="Salé">Salé</option>
                    <option value="Kénitra">Kénitra</option>
                    <option value="Safi">Safi</option>
                    <option value="Essaouira">Essaouira</option>
                    <option value="Ifrane">Ifrane</option>
                    <option value="Taza">Taza</option>
                    <option value="Tétouan">Tétouan</option>
                    <option value="Al Hoceïma">Al Hoceïma</option>
                    <option value="Chefchaouen">Chefchaouen</option>
                    <option value="Nador">Nador</option>
                    <option value="Berkane">Berkane</option>
                    <option value="Béni Mellal">Béni Mellal</option>
                    <option value="Khénifra">Khénifra</option>
                    <option value="Errachidia">Errachidia</option>
                    <option value="Ouarzazate">Ouarzazate</option>
                    <option value="Taroudant">Taroudant</option>
                    <option value="Tiznit">Tiznit</option>
                    <option value="Guelmim">Guelmim</option>
                    <option value="Laâyoune">Laâyoune</option>
                    <option value="Dakhla">Dakhla</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg">
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              {successMessage}
            </div>
          </div>
        )}

        {/* Supervisors Table */}
        <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 overflow-hidden">
          {supervisors.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <FontAwesomeIcon icon={faUsers} className="text-4xl text-gray-300 mb-4" />
              <p className="text-lg">Aucun prestataire trouvé</p>
              <p className="text-sm">Les prestataires apparaîtront ici</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Prestataire
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ville
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Région
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Statut
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Note Contrôle
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Dernière connexion
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {supervisors.map((supervisor) => (
                    <tr 
                      key={supervisor.id}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/supervisors/${supervisor.id}`)}
                    >
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
                              <FontAwesomeIcon icon={faUser} className="text-red-600" />
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {supervisor.name}
                            </div>
                            <div className="text-sm text-gray-500">
                              {supervisor.ref}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {supervisor.email}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {getSupervisorCity(supervisor)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {supervisor.region || '-'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {getStatusIcon(supervisor.status)}
                          <span className="ml-2 text-sm text-gray-900">
                            {getStatusText(supervisor.status)}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        {supervisor.controllers_rating != null ? (() => {
                          const avg = (parseFloat(supervisor.controllers_rating) / 100) * 5;
                          return (
                            <span className={`font-semibold ${
                              avg >= 4 ? 'text-green-600' :
                              avg >= 3 ? 'text-yellow-600' :
                              'text-red-600'
                            }`}>
                              {avg.toFixed(1)}/5
                            </span>
                          );
                        })() : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        {supervisor.last_login ? (
                          <div className="flex items-center">
                            <FontAwesomeIcon icon={faCalendarAlt} className="mr-2 text-gray-400" />
                            {new Date(supervisor.last_login).toLocaleDateString('fr-FR')}
                          </div>
                        ) : (
                          <span className="text-gray-400">Jamais connecté</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-between bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-4 gap-4">
            <div className="flex items-center text-sm text-gray-700">
                <span>
                  Affichage de {((currentPage - 1) * 10) + 1} à {Math.min(currentPage * 10, totalCount)} sur {totalCount} prestataires
                </span>
            </div>
            
            <div className="flex flex-col sm:flex-row items-center gap-3">
              {/* Mobile navigation */}
              <div className="flex sm:hidden space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <FontAwesomeIcon icon={faChevronLeft} className="w-4 h-4" />
                </button>
                
                <div className="flex items-center px-3 py-2 bg-red-600 text-white rounded-lg">
                  {currentPage} / {totalPages}
                </div>
                
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <FontAwesomeIcon icon={faChevronRight} className="w-4 h-4" />
                </button>
              </div>
              
              {/* Desktop navigation */}
              <div className="hidden sm:flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <FontAwesomeIcon icon={faChevronLeft} className="w-4 h-4" />
                </button>
                
                <div className="flex space-x-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                          currentPage === pageNum
                            ? 'bg-red-600 text-white'
                            : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <FontAwesomeIcon icon={faChevronRight} className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Supervisors;